import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ChevronDown } from 'lucide-react';
import HomeFeatures from '../components/HomeFeatures';

// Hero photo, self-hosted (Unsplash License — see LICENSES.md).
// Responsive variants come from scripts/make-hero-images.mjs; the same
// source photo feeds the social sharing card (scripts/make-og-image.mjs).
// The hero is the LCP element, so it is eagerly loaded at high priority.
const HERO_WIDTHS = [640, 960, 1280, 1920, 2400];
const heroSrcSet = (ext: 'webp' | 'jpg') =>
  HERO_WIDTHS.map((w) => `/hero/lighthouse-${w}.${ext} ${w}w`).join(', ');
const HERO_FALLBACK = '/hero/lighthouse-1280.jpg';
const HERO_ALT =
  'A lighthouse standing against a calm sky — structure and direction, the idea behind Met Maät';

const Home: React.FC = () => {
  const { t } = useLanguage();

  return (
    <div className="bg-maat-stone overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image with Controlled Overlay */}
        <div className="absolute inset-0 z-0">
          <picture className="block w-full h-full">
            <source type="image/webp" srcSet={heroSrcSet('webp')} sizes="100vw" />
            <img
              src={HERO_FALLBACK}
              srcSet={heroSrcSet('jpg')}
              sizes="100vw"
              width={2400}
              height={1471}
              fetchPriority="high"
              decoding="async"
              alt={HERO_ALT}
              className="w-full h-full object-cover"
            />
          </picture>
          <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px]"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-maat-stone"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10 py-32">
          <div className="max-w-3xl text-left animate-in fade-in slide-in-from-bottom-8 duration-1000 -mt-24 md:-mt-32">
            <h1 className="text-4xl md:text-6xl font-serif text-maat-blue mb-5 leading-tight drop-shadow-sm">
              {t.hero.title}
            </h1>
            <p className="text-xl md:text-3xl font-serif italic text-maat-gold mb-8 drop-shadow-sm">
              {t.hero.slogan}
            </p>
            <p className="text-lg md:text-xl text-maat-blue/80 leading-relaxed max-w-xl font-light mb-8">
              {t.hero.subtitle}
            </p>
            <button 
              onClick={() => document.getElementById('features-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-2 bg-maat-blue text-maat-stone px-6 py-3 rounded-full hover:bg-maat-gold hover:text-maat-blue transition-all duration-300 font-bold cursor-pointer shadow-lg transform hover:-translate-y-1"
            >
              {t.hero.readMore}
            </button>
          </div>
        </div>

        <button 
          onClick={() => document.getElementById('features-section')?.scrollIntoView({ behavior: 'smooth' })}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center space-y-2 opacity-50 hover:opacity-100 transition-opacity duration-300 animate-bounce cursor-pointer focus:outline-none"
          aria-label="Scroll down"
        >
          <div className="w-[1px] h-12 bg-maat-blue/50"></div>
          <ChevronDown className="text-maat-blue" size={24} />
        </button>
      </section>



      {/* What makes Met Maät so special */}
      <HomeFeatures />
    </div>
  );
};

export default Home;